import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Share } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import usePWAInstall, { isIosDevice } from "@/hooks/usePWAInstall";

/**
 * Floating install hint — now **iOS only**.
 *
 * Why the narrowing: this used to be the app's single install surface on every
 * platform and every route. The landing page and dashboard now each carry a
 * dedicated surface (see InstallApp.jsx) that opens the browser's real install
 * dialog in one click, so on Chromium this banner would be a third nag saying
 * the same thing, worse.
 *
 * iOS is the case those surfaces genuinely cannot serve: Safari has never
 * implemented `beforeinstallprompt`, so there is no dialog to open and
 * `canInstall` is permanently false there. Add-to-Home-Screen still works, but
 * only as a manual gesture through the Share sheet — which needs explaining.
 * That instruction is the one thing a native prompt can't replace, so it is all
 * this component does now.
 *
 * Install/dismiss state is shared with the other surfaces through
 * `usePWAInstall()`, so dismissing this silences all of them — except on
 * Landing and Dashboard, which this component skips entirely: both render
 * their own iOS instructions inline inside InstallApp.jsx's strip instead,
 * so every platform gets identical "always show on arrival, close-for-this-
 * visit-only" treatment there. Rendering this floating version there too
 * would just be a second, differently-styled copy of the same nudge stacked
 * on top of it.
 */

// Held back briefly so it doesn't land on top of a first paint.
const APPEAR_DELAY_MS = 4000;

// Routes where InstallApp.jsx's strip already covers iOS inline.
const STRIP_COVERED_ROUTES = ["/", "/dashboard"];
// Onboarding's own Back/Continue footer sits bottom-right on narrow
// screens — the same corner this card anchors to (bottom-24/28,
// left-4 right-4 below sm:). Confirmed by actually driving signup ->
// onboarding at a mobile viewport: the card physically covered the
// Continue button, same as WhatsAppSupportButton's bubble did. A
// short, mandatory wizard shouldn't have its one required button
// blocked by an install nudge.
const NUDGE_FREE_ROUTES = ["/onboarding"];

export default function InstallPrompt() {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const { isInstalled, isDismissed, dismiss } = usePWAInstall();
  const [delayElapsed, setDelayElapsed] = useState(false);

  const stripCovered = STRIP_COVERED_ROUTES.includes(pathname);
  const nudgeFree = NUDGE_FREE_ROUTES.includes(pathname);
  const relevant = !stripCovered && !nudgeFree && isIosDevice() && !isInstalled && !isDismissed;

  useEffect(() => {
    if (!relevant) return;
    const id = setTimeout(() => setDelayElapsed(true), APPEAR_DELAY_MS);
    return () => clearTimeout(id);
  }, [relevant]);

  if (!relevant || !delayElapsed) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      role="status"
      className="install-reveal fixed bottom-24 sm:bottom-28 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-sm z-50 rounded-2xl bg-white dark:bg-[#12221D] border border-[#E7E0D2] dark:border-[#22453A] shadow-[0_15px_50px_-15px_rgba(6,78,59,0.35)] p-4">
      <button
        type="button"
        onClick={dismiss}
        aria-label={t("install.dismiss")}
        data-testid="pwa-install-dismiss"
        className="absolute top-2 right-2 w-7 h-7 grid place-items-center rounded-full text-[#4B5563] dark:text-[#B7C2BB] transition hover:bg-[#FDFBF7] dark:hover:bg-[#16302A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D97706]">
        <X className="w-3.5 h-3.5" aria-hidden="true" />
      </button>

      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-xl bg-[#064E3B] grid place-items-center text-[#FED7AA] shrink-0"
          aria-hidden="true">
          <Share className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0 pr-5">
          <div className="font-heading text-sm font-semibold text-[#1F2937] dark:text-[#F3EFE4]">
            {t("install.ios_title")}
          </div>
          <div className="text-xs text-[#4B5563] dark:text-[#B7C2BB] mt-1 leading-relaxed">
            {t("install.ios_step_pre")}{" "}
            <span className="font-medium text-[#1F2937] dark:text-[#F3EFE4]">{t("install.ios_share")}</span>
            {" → "}
            <span className="font-medium text-[#1F2937] dark:text-[#F3EFE4]">{t("install.ios_add_to_home")}</span>{" "}
            {t("install.ios_step_post")}
          </div>
        </div>
      </div>
    </div>
  );
}
